/**
 * Storage Estimator config — mirrors `kitchenConfig.js` patterns so the
 * backend BOQ converter and PDF generator can read the same shape.
 *
 * iter-296: Per-estimate Hardware Rates / Electrical / Additional Hardware
 * panels with brand selection. Lets each quote match the actual vendor's
 * pricing without touching global defaults.
 */

// Brand presets (per-piece rates in Rs).
// Each option carries `id`, `label`, `rate`. `isCustom: true` lets the user
// type a free-text brand name + rate.
export const HARDWARE_BRANDS = {
  hinge: [
    { id: 'hettich', label: 'Hettich Standard', rate: 120 },
    { id: 'hettich_sc', label: 'Hettich Soft-Close', rate: 180 },
    { id: 'hafele', label: 'Hafele Standard', rate: 140 },
    { id: 'hafele_sc', label: 'Hafele Soft-Close', rate: 220 },
    { id: 'blum_sc', label: 'Blum Soft-Close', rate: 320 },
    { id: 'ebco', label: 'Ebco Standard', rate: 95 },
    { id: 'generic', label: 'Generic', rate: 60 },
    { id: 'custom', label: 'Custom (specify)', rate: 0, isCustom: true },
  ],
  straightener: [
    { id: 'hettich', label: 'Hettich', rate: 350 },
    { id: 'hafele', label: 'Hafele', rate: 420 },
    { id: 'ebco', label: 'Ebco', rate: 280 },
    { id: 'generic', label: 'Generic', rate: 220 },
    { id: 'custom', label: 'Custom (specify)', rate: 0, isCustom: true },
  ],
  telescopic: [
    { id: 'standard_450', label: 'Telescopic (Standard)', rate: 450 },
    { id: 'sc_750', label: 'Telescopic Soft-Close', rate: 750 },
    { id: 'tandem_2800', label: 'Tandem Drawer', rate: 2800 },
    { id: 'blum_4500', label: 'Tandem (Premium Blum)', rate: 4500 },
    { id: 'custom', label: 'Custom (specify)', rate: 0, isCustom: true },
  ],
  sliding_channel: [
    { id: 'hettich', label: 'Hettich Top-Mount', rate: 4500 },
    { id: 'hafele', label: 'Hafele Top-Mount', rate: 5400 },
    { id: 'blum_pro', label: 'Blum Pro Sliding', rate: 7800 },
    { id: 'ebco', label: 'Ebco Bottom-Mount', rate: 3200 },
    { id: 'generic', label: 'Generic', rate: 2200 },
    { id: 'custom', label: 'Custom (specify)', rate: 0, isCustom: true },
  ],
  handle: [
    { id: 'gola_aluminum', label: 'Gola Aluminium Profile', rate: 250 },
    { id: 'cabinet_handle', label: 'Cabinet Handle (160mm)', rate: 180 },
    { id: 'tubular_pull', label: 'Tubular Pull (256mm)', rate: 320 },
    { id: 'edge_pull', label: 'Edge Pull (no handle)', rate: 0 },
    { id: 'knob', label: 'Designer Knob', rate: 220 },
    { id: 'custom', label: 'Custom (specify)', rate: 0, isCustom: true },
  ],
};

// Electrical line items (mirrors Kitchen ELECTRICAL_ITEMS).
// `unit` is informational; `rate` is editable per estimate.
export const STORAGE_ELECTRICAL_ITEMS = [
  { id: 'led_strip', label: 'LED Strip Light', rate: 560, unit: 'per ft' },
  { id: 'led_profile', label: 'Under-Shelf LED Profile', rate: 760, unit: 'per ft' },
  { id: 'driver', label: 'LED Driver (60W)', rate: 2400, unit: 'each' },
  { id: 'sensor', label: 'Motion Sensor Switch', rate: 1700, unit: 'each' },
  { id: 'socket', label: 'Wardrobe Socket Point', rate: 900, unit: 'each' },
  { id: 'drawer_light', label: 'Drawer Light', rate: 1100, unit: 'each' },
  { id: 'shutter_led', label: 'In-Shutter LED Bar', rate: 1450, unit: 'each' },
];

// Additional hardware (extras beyond hinge/straightener/telescopic/sliding).
export const STORAGE_EXTRA_HARDWARE = [
  { id: 'gas_lift', label: 'Gas Lift / Lift-Up Stay', rate: 950, unit: 'each' },
  { id: 'mortise_lock', label: 'Mortise Lock', rate: 1200, unit: 'each' },
  { id: 'wardrobe_lock', label: 'Wardrobe Lock', rate: 480, unit: 'each' },
  { id: 'push_to_open', label: 'Push-to-Open Mechanism', rate: 380, unit: 'each' },
  { id: 'magnet_catch', label: 'Magnet Catch', rate: 45, unit: 'each' },
  { id: 'shelf_support', label: 'Shelf Pin / Support', rate: 8, unit: 'each' },
  { id: 'connector_bolt', label: 'Connector Bolt', rate: 12, unit: 'each' },
  { id: 'skirting', label: 'PVC Skirting', rate: 40, unit: 'per ft' },
  { id: 'leveller_leg', label: 'Adjustable Leveller / Leg', rate: 60, unit: 'each' },
];

export const STORAGE_HARDWARE_DEFAULTS = {
  electricianLaborPct: 15, // % of electrical material cost
};

// iter-300: Brand Portfolio Presets — one-click hardware bundles that swap
// hinge / straightener / telescopic / sliding / handle brands + rates.
// Tier picks the MATCHING `HARDWARE_BRANDS[x]` id so the Brand Select and
// the Rate input both update together.
// iter-302: Premium now uses Blum (top-tier global brand) so the "Premium"
// tier is GENUINELY the most expensive. Mid-range uses Hafele Soft-Close;
// Standard uses Hettich Soft-Close; Economy uses Ebco. This fixes the
// confusing case where Hettich Premium ended up cheaper than Hafele Mid.
export const BRAND_PORTFOLIO_PRESETS = [
  {
    id: 'blum_premium',
    label: 'Blum Premium',
    tag: 'Premium',
    tone: 'amber',
    description: 'Blum German engineering. For luxury clients who notice the details.',
    picks: {
      hinge: 'blum_sc',           // Rs 320
      straightener: 'hafele',     // Rs 420 (highest straightener)
      telescopic: 'tandem_2800',  // Rs 2,800 (Tandem)
      sliding_channel: 'blum_pro', // Rs 7,800
      handle: 'tubular_pull',     // Rs 320
    },
  },
  {
    id: 'hafele_mid',
    label: 'Hafele Mid-range',
    tag: 'Balanced',
    tone: 'blue',
    description: 'Hafele quality at a sensible price. Safe default for most quotes.',
    picks: {
      hinge: 'hafele_sc',         // Rs 220
      straightener: 'hafele',     // Rs 420
      telescopic: 'sc_750',       // Rs 750
      sliding_channel: 'hafele',  // Rs 5,400
      handle: 'cabinet_handle',   // Rs 180
    },
  },
  {
    id: 'hettich_standard',
    label: 'Hettich Standard',
    tag: 'Standard',
    tone: 'emerald',
    description: 'Reliable Hettich Soft-Close. The everyday workhorse for most projects.',
    picks: {
      hinge: 'hettich_sc',        // Rs 180
      straightener: 'hettich',    // Rs 350
      telescopic: 'sc_750',       // Rs 750
      sliding_channel: 'hettich', // Rs 4,500
      handle: 'cabinet_handle',   // Rs 180
    },
  },
  {
    id: 'ebco_economy',
    label: 'Ebco Economy',
    tag: 'Economy',
    tone: 'stone',
    description: 'Budget-friendly Ebco / Generic. Great for bulk / investor projects.',
    picks: {
      hinge: 'ebco',              // Rs 95
      straightener: 'ebco',       // Rs 280
      telescopic: 'standard_450', // Rs 450
      sliding_channel: 'ebco',    // Rs 3,200
      handle: 'gola_aluminum',    // Rs 250
    },
  },
];

// Helper: given a preset id, resolve to {brand_key → {id, label, rate}} for
// each hardware category so WardrobeCalculator can push all state at once.
export function resolvePreset(presetId) {
  const preset = BRAND_PORTFOLIO_PRESETS.find(p => p.id === presetId);
  if (!preset) return null;
  const out = {};
  Object.entries(preset.picks).forEach(([cat, optionId]) => {
    const option = (HARDWARE_BRANDS[cat] || []).find(o => o.id === optionId);
    if (option) out[cat] = { id: option.id, label: option.label, rate: option.rate };
  });
  return { ...preset, resolved: out };
}
